import { useMutation, useQuery } from '@apollo/client';
import { ButtonDock, miniButtonDockScrollBottomPad } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Caption, Text } from '@telegram-apps/telegram-ui';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    GetTemplatesQuery,
    GetTemplatesQueryVariables,
    InitializeOnboardingMutation,
    InitializeOnboardingMutationVariables,
} from '../../client/generated/graphql';
import { initializeOnboardingMutation } from '../../client/mutations/initializeOnboarding';
import { getTemplatesQuery } from '../../client/queries/template';
import { CommonIcon } from '../../components/common/CommonIcon';
import { FocusLeePlaceholderImage } from '../../components/common/FocusLeePlaceholderImage';
import { LiquidGlassRowCard } from '../../components/common/LiquidGlassRowCard';
import { Page } from '../../components/Page';
import { getUserTimezone } from '../../helpers/datetime';
import { onboardingCategorySelectionPath } from '../../navigation/paths';

const features = [
    {
        icon: 'task_alt',
        title: 'onboarding.features.completeQuests.title',
        description: 'onboarding.features.completeQuests.description',
    },
    {
        icon: 'emoji_events',
        title: 'onboarding.features.earnXp.title',
        description: 'onboarding.features.earnXp.description',
    },
    {
        icon: 'analytics',
        title: 'onboarding.features.trackProgress.title',
        description: 'onboarding.features.trackProgress.description',
    },
] as const;

export const OnBoardingWelcomePage: FC = () => {
    const { t } = useTranslation('common');
    const { data, loading } = useQuery<GetTemplatesQuery, GetTemplatesQueryVariables>(getTemplatesQuery);
    const [initializeOnboarding, { loading: initializingOnboarding }] = useMutation<
        InitializeOnboardingMutation,
        InitializeOnboardingMutationVariables
    >(initializeOnboardingMutation);
    const initializationStarted = useRef(false);
    const templates = useMemo(() => data?.templateCategories.filter(template => !template.system), [data?.templateCategories]);
    const navigate = useNavigate();

    useEffect(() => {
        if (initializationStarted.current) return;
        initializationStarted.current = true;

        const initialize = async () => {
            try {
                await initializeOnboarding({ variables: { timezone: getUserTimezone() } });
            } catch (error) {
                console.error('Failed to initialize onboarding:', error);
                initializationStarted.current = false;
            }
        };

        initialize();
    }, [initializeOnboarding]);

    const isLoading = loading || initializingOnboarding;
    const handleContinue = useCallback(() => {
        navigate(onboardingCategorySelectionPath, { state: { templates } });
    }, [navigate, templates]);

    return (
        <Page
            back={false}
            insetEnabled={false}
            edgeToEdge
            footer={
                <ButtonDock
                    main={{
                        text: t('actions.continue'),
                        onClick: handleContinue,
                        enabled: !isLoading && !!templates,
                        loading: isLoading,
                    }}
                />
            }
        >
            <Box
                sx={{
                    position: 'relative',
                    height: '100%',
                    overflow: 'hidden',
                    bgcolor: 'var(--tg-theme-secondary-bg-color)',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        WebkitOverflowScrolling: 'touch',
                        paddingTop:
                            'calc(var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px) + 16px)',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        paddingBottom: miniButtonDockScrollBottomPad,
                    }}
                >
                    <Stack alignItems="center" spacing={1.5}>
                        <FocusLeePlaceholderImage kind="onboarding" size={112} />
                        <Text weight="2" style={{ fontSize: 28, lineHeight: 1.1, textAlign: 'center' }}>
                            {t('onboarding.welcomeTitle')}
                        </Text>
                        <Caption
                            style={{
                                maxWidth: 300,
                                color: 'var(--tg-theme-hint-color)',
                                fontSize: 15,
                                lineHeight: '20px',
                                textAlign: 'center',
                            }}
                        >
                            {t('onboarding.welcomeDescription')}
                        </Caption>
                        <Stack spacing={1} sx={{ width: '100%', pt: 1 }}>
                            {features.map(feature => (
                                <LiquidGlassRowCard key={feature.title} borderRadius={16}>
                                    <Stack direction="row" alignItems="center" gap={1.5} sx={{ p: 1.5 }}>
                                        <Box
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                flexShrink: 0,
                                                borderRadius: '14px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: 'color-mix(in srgb, var(--tg-theme-button-color) 14%, transparent)',
                                            }}
                                        >
                                            <CommonIcon icon={feature.icon} color="var(--tg-theme-button-color)" size={26} type="filled" />
                                        </Box>
                                        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                            <Text weight="2" style={{ fontSize: 15, lineHeight: 1.2 }}>
                                                {t(feature.title)}
                                            </Text>
                                            <Caption style={{ color: 'var(--tg-theme-hint-color)', fontSize: 12, lineHeight: '16px' }}>
                                                {t(feature.description)}
                                            </Caption>
                                        </Stack>
                                    </Stack>
                                </LiquidGlassRowCard>
                            ))}
                        </Stack>
                    </Stack>
                </Box>
            </Box>
        </Page>
    );
};
